/**
 * Whether a user's tracked bets have proven an edge over the model (backlog 240). The server
 * stores it per user; the apps show the strategy score only for the two proven states.
 */
import { isTeamBet } from './correlationUtils';
import type { ParlayScore } from './hooks/useParlayScoring';
import type { LeagueKey } from './league';
import { formatStatLabel } from './statLabels';
import { getTeamAbbr } from './teams';

export const EDGE_STATES = ['not_proven', 'proven_above', 'proven_below'] as const;

export type EdgeState = (typeof EDGE_STATES)[number];

/** An edge is proven at 0.99 and stays on while it clears 0.95. */
export const EDGE_CONFIDENCE_LEVELS = [0.99, 0.95] as const;

export type EdgeConfidence = (typeof EDGE_CONFIDENCE_LEVELS)[number];

/** GET /api/scoring/edge-status. A locked tier gets nulls: nothing behind the lock is sent. */
export interface EdgeStatusView {
  locked: boolean;
  state: EdgeState | null;
  betsCounted: number | null;
  /** Proven edge per leg, the end of its interval nearest zero; null unless proven. */
  bound: number | null;
  /** The level the proven edge clears now, which `bound` is taken at; null unless proven. */
  confidence: EdgeConfidence | null;
}

/** The personal score's label on every surface (approved 2026-10-13). */
export const PERSONAL_SCORE_LABEL = 'With your edge';

/** What the strategy score's badge slot shows, in the wording approved for section 05. */
export type EdgeBadge =
  | { kind: 'locked'; label: string; hint: string }
  | { kind: 'not_proven'; label: string; hint: string }
  | { kind: 'proven'; below: boolean; label: string; hint: string };

export function edgeBadge(status: EdgeStatusView | null): EdgeBadge | null {
  if (!status) return null;
  if (status.locked) {
    return {
      kind: 'locked',
      label: 'Your edge vs the AI',
      hint: 'See whether your bets beat the AI. Pro and up.',
    };
  }
  if (status.state === 'proven_above' || status.state === 'proven_below') {
    if (status.bound === null || status.confidence === null) return null;
    const below = status.state === 'proven_below';
    // Rounded down: the text promises "at least" this many points.
    const points = (Math.floor(Math.abs(status.bound) * 1000 + 1e-9) / 10).toFixed(1);
    const proof =
      status.confidence === 0.99
        ? `by at least ${points} points per bet (proven at 99%).`
        : `by at least ${points} points per bet. It was proven at 99% and now holds at 95%.`;
    return {
      kind: 'proven',
      below,
      label: PERSONAL_SCORE_LABEL,
      hint: `Your tracked bets have hit ${below ? 'less' : 'more'} often than the AI expected, ${proof} This score assumes that continues: it ${below ? 'lowers' : 'raises'} the AI's chance on each leg by about ${points} points at even odds, and by less for long shots and heavy favorites.`,
    };
  }
  const bets = status.betsCounted ?? 0;
  return {
    kind: 'not_proven',
    label: `Edge not proven yet (${bets.toLocaleString('en-US')} settled ${bets === 1 ? 'bet' : 'bets'})`,
    hint: 'Once your tracked bets beat or trail the AI by more than luck can explain, your personal score appears here. The bigger the edge, the sooner it shows: in our simulations a 5-point edge took about 1,200 bets, a 3-point edge about 2,300, and most 2-point edges never showed within 3,850.',
  };
}

function isEdgeState(value: unknown): value is EdgeState {
  return EDGE_STATES.some((state) => state === value);
}

function isEdgeConfidence(value: unknown): value is EdgeConfidence {
  return EDGE_CONFIDENCE_LEVELS.some((level) => level === value);
}

function numberOrNull(value: unknown): number | null | undefined {
  if (value === null) return null;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function field(body: object, key: string): unknown {
  return key in body ? Reflect.get(body, key) : undefined;
}

/** The edge-status response, or null when it is not one. */
export function readEdgeStatus(body: unknown): EdgeStatusView | null {
  if (typeof body !== 'object' || body === null) return null;
  const locked = field(body, 'locked');
  const state = field(body, 'state');
  const bets = numberOrNull(field(body, 'betsCounted'));
  const edge = numberOrNull(field(body, 'bound'));
  const confidence = field(body, 'confidence');
  if (typeof locked !== 'boolean' || bets === undefined || edge === undefined) return null;
  if (state !== null && !isEdgeState(state)) return null;
  if (confidence !== null && !isEdgeConfidence(confidence)) return null;
  return { locked, state, betsCounted: bets, bound: edge, confidence };
}

/** A leg as a score panel knows it: enough to name it. */
export interface EdgeLegSource {
  player?: string | null;
  team?: string | null;
  statType?: string | null;
  overUnder?: string | null;
  line?: number | null;
  leagueKey?: LeagueKey | null;
}

/** One leg's AI chance and its chance with the user's proven edge. */
export interface EdgeLegChange {
  label: string;
  ai: number;
  withEdge: number;
}

/** "LeBron James Over 25.5 Points", "CLE -1.5 Spread", "CLE Moneyline", "CLE @ ORL Over 230.5 Total". */
export function edgeLegLabel(leg: EdgeLegSource): string {
  const abbr = (team: string) => getTeamAbbr(team, leg.leagueKey);
  // A game-level leg is named by its team, or both teams for a total ("CLE @ ORL"); the builder
  // keeps a placeholder player on those legs.
  const sides = leg.team?.split(/ @ | vs /i) ?? [];
  const name = isTeamBet(leg.statType)
    ? sides.length === 2
      ? `${abbr(sides[0])} @ ${abbr(sides[1])}`
      : leg.team
        ? abbr(leg.team)
        : ''
    : leg.player || leg.team || '';
  const line =
    leg.line == null
      ? ''
      : leg.overUnder
        ? ` ${leg.line}`
        : ` ${leg.line > 0 ? '+' : ''}${leg.line}`;
  const direction = leg.overUnder ? ` ${leg.overUnder}` : '';
  const stat = leg.statType ? ` ${formatStatLabel(leg.statType)}` : '';
  return `${name}${direction}${line}${stat}`.trim();
}

/** Each leg's AI chance beside its chance with the edge, paired by leg key; null unless the two
 * lists pair one to one, since no row beats a wrong row. */
export function edgeLegChanges(
  aiScore: ParlayScore | null,
  personalScore: ParlayScore | null,
  legs?: ReadonlyArray<EdgeLegSource>,
): EdgeLegChange[] | null {
  const ai = aiScore?.legProbabilities;
  const personal = personalScore?.legProbabilities;
  if (!ai?.length || !personal || personal.length !== ai.length) return null;
  const withEdgeByKey = new Map(personal.map((lp) => [lp.legKey, lp.probability]));
  if (withEdgeByKey.size !== personal.length) return null;
  const named = legs?.length === ai.length ? legs : undefined;
  const rows: EdgeLegChange[] = [];
  for (const [i, lp] of ai.entries()) {
    const withEdge = withEdgeByKey.get(lp.legKey);
    if (withEdge === undefined) return null;
    const label = named?.[i] ? edgeLegLabel(named[i]) : '';
    rows.push({ label: label || `Leg ${i + 1}`, ai: lp.probability, withEdge });
  }
  return rows;
}

/** A probability as a percentage with one decimal: 0.6204 reads "62.0%". */
export function formatChance(probability: number): string {
  return `${(probability * 100).toFixed(1)}%`;
}
